// Read-back in the browser's own voice (`speechSynthesis`), each word lit as
// it is spoken. The reader's Polly voice is out of scope for the lab, so the
// browser's is what a device has; where it reports word boundaries the
// highlight follows them, and where it does not (Safari fires none, from
// memory, unverified) the highlight runs on a schedule proportional to each
// word's length. Pure parts first; the hook at the end is the only thing
// that touches the DOM.
import { useEffect, useRef, useState } from "react";
import type { SpokenWord } from "./ladder";

// Rate 0.9 reads a shade slower than the default for a child following
// along; the fallback schedule is tuned to it.
export const READ_RATE = 0.9;
const MS_PER_CHAR_AT_RATE_1 = 70;
const WORD_FLOOR_MS = 180;
const PAUSE_BETWEEN_WORDS_MS = 60;

/** Each word's start offset within the utterance, in ms, from its length alone. */
export function wordSchedule(
  words: readonly string[],
  rate: number = READ_RATE,
): number[] {
  const msPerChar = MS_PER_CHAR_AT_RATE_1 / rate;
  return words.reduce<number[]>((offsets, word, i) => {
    const previous = words[i - 1];
    const start =
      previous === undefined
        ? 0
        : (offsets[i - 1] ?? 0) +
          Math.max(WORD_FLOOR_MS, previous.length * msPerChar) +
          PAUSE_BETWEEN_WORDS_MS;
    return [...offsets, start];
  }, []);
}

/** The text spoken, and where each word starts in it, for boundary events. */
export function utteranceText(words: readonly string[]): {
  text: string;
  charOffsets: number[];
} {
  const charOffsets = words.reduce<number[]>(
    (acc, _, i) => [
      ...acc,
      i === 0 ? 0 : (acc[i - 1] ?? 0) + (words[i - 1]?.length ?? 0) + 1,
    ],
    [],
  );
  return { text: words.join(" "), charOffsets };
}

/** The word a boundary event's `charIndex` falls in. */
export function wordAtChar(
  charOffsets: readonly number[],
  charIndex: number,
): number {
  const after = charOffsets.findIndex((offset) => offset > charIndex);
  return Math.max(0, (after < 0 ? charOffsets.length : after) - 1);
}

export interface Reading {
  readonly words: readonly SpokenWord[];
  /** Index of the word being spoken; null between words or before the first. */
  readonly index: number | null;
}

/**
 * How an utterance ended. A chain that does something after a read-back
 * (open the microphone, speak the next cue) must look: a cancel resolves
 * the promise too, and carrying on would act on the child's interruption.
 */
export type SpeakOutcome = "done" | "cancelled";

/**
 * Runs each step once the one before it finished. A step cut short ends
 * the chain, so the word after a clip the child interrupted is never
 * spoken over what they turned to.
 */
export const inTurn = (
  steps: readonly (() => Promise<SpeakOutcome>)[],
): Promise<SpeakOutcome> =>
  steps.reduce<Promise<SpeakOutcome>>(
    (chain, step) =>
      chain.then((outcome) => (outcome === "done" ? step() : outcome)),
    Promise.resolve("done"),
  );

export interface ReadAloud {
  readonly reading: Reading | null;
  /** Speaks the words, lighting each; resolves when done or cancelled. */
  readonly read: (words: readonly SpokenWord[]) => Promise<SpeakOutcome>;
  /** A short spoken cue with nothing to light. */
  readonly say: (text: string) => Promise<SpeakOutcome>;
  readonly cancel: () => void;
}

const synth = (): SpeechSynthesis | null =>
  typeof window !== "undefined" && "speechSynthesis" in window
    ? window.speechSynthesis
    : null;

interface Playing {
  readonly timers: ReturnType<typeof setTimeout>[];
  readonly finish: (outcome: SpeakOutcome) => void;
}

export const useReadAloud = (): ReadAloud => {
  const [reading, setReading] = useState<Reading | null>(null);
  const playing = useRef<Playing | null>(null);

  const cancel = (): void => {
    const current = playing.current;
    if (current === null) return;
    playing.current = null;
    for (const timer of current.timers) clearTimeout(timer);
    synth()?.cancel();
    current.finish("cancelled");
    setReading(null);
  };

  const speak = (
    words: readonly SpokenWord[],
    onWord: (index: number) => void,
  ): Promise<SpeakOutcome> =>
    new Promise((resolve) => {
      cancel();
      const texts = words.map((w) => w.text);
      const { text, charOffsets } = utteranceText(texts);
      const schedule = wordSchedule(texts);
      const totalMs =
        (schedule[schedule.length - 1] ?? 0) +
        Math.max(WORD_FLOOR_MS, (texts[texts.length - 1]?.length ?? 0) * 80);
      let settled = false;
      const timers: ReturnType<typeof setTimeout>[] = [];
      const finish = (outcome: SpeakOutcome) => {
        if (settled) return;
        settled = true;
        resolve(outcome);
      };
      const session: Playing = { timers, finish };
      playing.current = session;

      // The schedule runs until the voice proves it reports boundaries; then
      // the events take over and the timers are dropped.
      let boundariesSeen = false;
      for (const [i, offset] of schedule.entries()) {
        timers.push(
          setTimeout(() => {
            if (!boundariesSeen && playing.current === session) onWord(i);
          }, offset),
        );
      }
      const end = () => {
        if (playing.current !== session) return;
        playing.current = null;
        for (const timer of timers) clearTimeout(timer);
        setReading(null);
        finish("done");
      };

      const voice = synth();
      if (voice === null || text === "") {
        timers.push(setTimeout(end, totalMs));
        return;
      }
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = READ_RATE;
      utterance.lang = "en-US";
      utterance.onboundary = (event) => {
        if (event.name !== "word" || playing.current !== session) return;
        boundariesSeen = true;
        onWord(wordAtChar(charOffsets, event.charIndex));
      };
      utterance.onend = end;
      utterance.onerror = end;
      voice.speak(utterance);
      // A voice that never fires `end` (seen on some Chromium builds after a
      // cancel, from memory) would otherwise leave the highlight stuck.
      timers.push(setTimeout(end, totalMs * 2 + 2000));
    });

  const read = (words: readonly SpokenWord[]): Promise<SpeakOutcome> => {
    setReading({ words, index: null });
    return speak(words, (index) => setReading({ words, index }));
  };

  const say = (text: string): Promise<SpeakOutcome> =>
    speak(
      text
        .split(/\s+/)
        .filter((w) => w.length > 0)
        .map((w) => ({ tokenId: "", text: w })),
      () => undefined,
    );

  // Unmounting mid-sentence must silence the voice and drop its timers.
  useEffect(() => cancel, []);

  return { reading, read, say, cancel };
};
